import React from 'react';
import {
  Blog as BlogType,
  UploadFile,
  BlogBlogDynamicDynamicZone,
  Author,
  Event,
} from '../../../types/schema';
import { useRouter } from 'next/router';
import BlogPostTemplate from './template';
import { ArticleJsonLd, ArticleJsonLdProps, BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { additionalLinkTags, breadcrumbTitle, SEO } from '../../../utils/SEO';

type BlogPostComponentProps = {
  metaTitle: string;
  metaDescription: string;
  metaImage?: string;
  hideAppMastersTitle?: boolean;
  language: string;
  slug: string;
  title: string;
  description: string;
  publicationDate: string;
  relatedPosts: BlogType[];
  postAuthor: Author;
  date: string;
  updatedAt: string;
  createdAt: string;
  blogDynamic: BlogBlogDynamicDynamicZone[];
  featuredImage?: UploadFile;
  events: Event[];
};

const BlogPostComponent: React.FC<BlogPostComponentProps> = ({
  metaTitle,
  metaDescription,
  metaImage,
  hideAppMastersTitle,
  featuredImage,
  language,
  title,
  description,
  publicationDate,
  postAuthor,
  relatedPosts,
  updatedAt,
  createdAt,
  blogDynamic,
  events,
}) => {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000/';
  const router = useRouter();
  const isEn = language === 'en';

  const itemListElements = [
    {
      position: 1,
      name: 'App Masters',
      item: `${siteUrl}${router.locale}`,
    },
    {
      position: 2,
      name: breadcrumbTitle('Blog', isEn),
      item: `${siteUrl}${router.locale}/blog`,
    },
    {
      position: 3,
      name: breadcrumbTitle(metaTitle || title, isEn),
      item: siteUrl + language + router.asPath,
    },
  ];

  const blogSeo: ArticleJsonLdProps = {
    type: 'Blog',
    url: siteUrl + language + router.asPath,
    title: title || metaTitle,
    images: [(featuredImage?.url || metaImage) as string],
    datePublished: createdAt,
    dateModified: updatedAt,
    authorName: postAuthor ? (postAuthor.name as string) : 'App Masters',
    description: description || metaDescription,
  };

  return (
    <>
      <NextSeo
        {...SEO({
          pathname: router.asPath,
          locale: router.locale as string,
          title: (metaTitle || title) as string,
          defaultTitle: {
            enTitle: 'Post',
            ptTitle: 'Post',
          },
          description: (metaDescription || description) as string,
          image: (metaImage || featuredImage?.url) as string,
          hideAppMastersTitle: hideAppMastersTitle as boolean,
        })}
        additionalLinkTags={additionalLinkTags(['blog', 'blog'])}
      />
      <BreadcrumbJsonLd itemListElements={itemListElements} />
      <ArticleJsonLd {...blogSeo} />
      <BlogPostTemplate
        language={language}
        relatedPosts={relatedPosts}
        title={title}
        postAuthor={postAuthor}
        blogDynamic={blogDynamic}
        publicationDate={publicationDate}
        featuredImage={featuredImage}
        events={events}
      />
    </>
  );
};

export default BlogPostComponent;
